import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styles from '../styles/Blog.module.css';
import blogPostStyles from '../styles/BlogPost.module.css';
import { FaArrowLeft } from 'react-icons/fa';
import { blogPosts } from '../blogs/BlogContent';
import Breadcrumbs from '../components/Breadcrumbs';
import WebApplicationSchema from '../components/WebApplicationSchema';
import FAQPageSchema from '../components/FAQPageSchema';
import { Helmet } from 'react-helmet';
import { Navigate } from 'react-router-dom';

// Blog list component
export default function Blog() {
  const { slug } = useParams();

   const features = [
    {
      title: "AI-Powered Hairstyles",
      description: "Explore the latest hairstyle trends, tips, and inspirations on the AI Hairstyle Studio Blog! Discover expert advice, virtual styling guides, and AI-powered insights to help you find your perfect look."
    },
    {
      title: "Trending Hairstyles",
      description: "Choose from hundreds of trendy hairstyles for both men and women, or upload your own reference."
    },
    {
      title: "Realistic Looks",
      description: "Our advanced AI ensures natural-looking results that match your facial features perfectly."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "AI Hairstyle Studio Salon",
    "description": "Explore the latest hairstyle trends, tips, and inspirations on the AI Hairstyle Studio Blog! Discover expert advice, virtual styling guides, and AI-powered insights to help you find your perfect look.",
    "applicationCategory": "Hairstyle and Grooming",
    "featureList": features.map(f => f.title).join(", ")
  };



  // If we have a slug, show the individual blog post
  if (slug) {
    const post = blogPosts.find(post => post.slug === slug);
    if (!post) return <Navigate to="/404" />;

    const title = post.frontmatter.title;
    const description = post.frontmatter.excerpt;

    return (
      <>
      <Helmet>
        <WebApplicationSchema />
        <Breadcrumbs />
        <FAQPageSchema />
        <title>{title}</title>
        <meta name="description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <div className={blogPostStyles.blogPost}>
        <Link to="/hairstyles" className={blogPostStyles.backLink}>
          <FaArrowLeft></FaArrowLeft>
        </Link>
        
        {post.frontmatter.image && (
          <div className={blogPostStyles.featuredImage}>
            <img
              src={post.frontmatter.image}
              alt={post.frontmatter.title}
              className={blogPostStyles.postImage}
            />
          </div>
        )}
        
        <h5 className={blogPostStyles.title}>{post.frontmatter.title}</h5>
        <p className={blogPostStyles.date}>{post.frontmatter.date}</p>
        
        <div className={blogPostStyles.content}>
          <ReactMarkdown 
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className={blogPostStyles.heading1} {...props} />,
              h2: ({node, ...props}) => <h2 className={blogPostStyles.heading2} {...props} />,
              h3: ({node, ...props}) => <h3 className={blogPostStyles.heading3} {...props} />,
              p: ({node, ...props}) => <p className={blogPostStyles.paragraph} {...props} />,
              a: ({node, ...props}) => <a className={blogPostStyles.link} {...props} />,
              ul: ({node, ...props}) => <ul className={blogPostStyles.list} {...props} />,
              ol: ({node, ...props}) => <ol className={blogPostStyles.list} {...props} />,
              li: ({node, ...props}) => <li className={blogPostStyles.listItem} {...props} />,
              code: ({node, inline, ...props}) => 
                inline ? (
                  <code className={blogPostStyles.inlineCode} {...props} />
                ) : (
                  <code className={blogPostStyles.codeBlock} {...props} />
                ),
              img: ({node, ...props}) => (
                <img className={blogPostStyles.image} {...props} alt={props.alt || ''} />
              ),
              blockquote: ({node, ...props}) => (
                <blockquote className={blogPostStyles.blockquote} {...props} />
              ),
            }}
          >
            {post.content}
          </ReactMarkdown>
        </div>
      </div>
            </>
    );
  }

  // Otherwise, show the blog list
  return (
    <>
     <Helmet>
        <WebApplicationSchema />
        <Breadcrumbs />
        <FAQPageSchema />
        <title>AI Hairstyle Studio - Best Hairstyles</title>
        <meta name="description" content="Explore the latest hairstyle trends, tips, and inspirations on the AI Hairstyle Studio Blog! Discover expert advice, virtual styling guides, and AI-powered insights to help you find your perfect look." />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
    <div className={styles.blogContainer}>
      <h1 className={styles.mainTitle}>Best Hairstyles</h1>
      <p className = "text-gray-500 text-lg mb-6 animate-slide-up text-center">Find the Best Hairstyles for Men and Female</p>
      <div className={styles.blogGrid}>
        {blogPosts.map((post) => (
          <div key={post.slug} className={styles.blogCard}>
            {post.frontmatter.image && (
              <div className={styles.imageWrapper}>
                <img
                  src={post.frontmatter.image}
                  alt={post.frontmatter.title}
                  className={styles.blogImage}
                />
              </div>
            )}
            <div className={styles.blogContent}>
              <h2 className={styles.blogTitle}>{post.frontmatter.title}</h2>
              <p className={styles.date}>{post.frontmatter.date}</p>
              <p className={styles.excerpt}>{post.frontmatter.excerpt}</p>
              <Link to={`/hairstyles/${post.slug}`} className={styles.readMore}>
                Read More →
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
          </>
  );
}
